import React, { useRef, useState } from "react";
import "../styles/BulkStudentUpload.css";

export default function BulkStudentUpload({ token }) {
  // Student upload
  const [studentFile, setStudentFile] = useState(null);
  const [studentMessage, setStudentMessage] = useState("");
  const [studentUploading, setStudentUploading] = useState(false);
  const [uploadDetails, setUploadDetails] = useState([]);
  const studentInputRef = useRef();

  // Fee upload
  const [feeFile, setFeeFile] = useState(null);
  const [feeMessage, setFeeMessage] = useState("");
  const [feeUploading, setFeeUploading] = useState(false);
  const feeInputRef = useRef();

  // --- Student Upload Handlers ---
  const handleStudentFileChange = (e) => {
    setStudentFile(e.target.files[0]);
    setStudentMessage("");
    setUploadDetails([]);
  };
  const handleStudentUpload = async (e) => {
    e.preventDefault();
    setStudentMessage("");
    setUploadDetails([]);
    if (!studentFile) return setStudentMessage("Please select an Excel file.");
    setStudentUploading(true);
    const formData = new FormData();
    formData.append("file", studentFile);
    try {
      const res = await fetch("/api/students/bulk-upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setStudentMessage(`✅ Upload successful! ${data.message || ""}`);
        if (data.errors && data.errors.length > 0) {
          setUploadDetails(data.errors);
        }
      } else {
        setStudentMessage("❌ " + (data.error || "Upload failed."));
        setUploadDetails(data.errors || []);
      }
    } catch (e) {
      setStudentMessage("❌ Upload failed. Try again.");
    }
    setStudentUploading(false);
    setStudentFile(null);
    if (studentInputRef.current) studentInputRef.current.value = "";
  };

  // --- Fee Upload Handlers ---
  const handleFeeFileChange = (e) => {
    setFeeFile(e.target.files[0]);
    setFeeMessage("");
  };
  const handleFeeUpload = async (e) => {
    e.preventDefault();
    if (!feeFile) return setFeeMessage("Please select an Excel file.");
    setFeeUploading(true);
    setFeeMessage("");
    const formData = new FormData();
    formData.append("file", feeFile);
    try {
      const res = await fetch("/api/fees/bulk-upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setFeeMessage("✅ Upload successful!");
      } else {
        setFeeMessage("❌ " + (data.error || "Upload failed."));
      }
    } catch (e) {
      setFeeMessage("❌ Upload failed. Try again.");
    }
    setFeeUploading(false);
    setFeeFile(null);
    if (feeInputRef.current) feeInputRef.current.value = "";
  };

  // --- Download Handlers ---
  const downloadTemplate = (type) => {
    const url =
      type === "student"
        ? "/templates/student_upload_template.xlsx"
        : "/templates/fee_upload_template.xlsx";
    window.open(url, "_blank");
  };
  const downloadData = (type) => {
    const url =
      type === "student"
        ? "/api/students/export"
        : "/api/fees/export";
    window.open(url, "_blank");
  };

  return (
    <div className="bulk-upload-wrapper">
      <h2>Bulk Import / Export</h2>
      <div className="bulk-section">
        <h3>1. Student Bulk Upload/Export</h3>
        <div className="bulk-actions-row">
          <button className="bulk-btn" onClick={() => downloadTemplate("student")}>
            Download Student Excel Template
          </button>
          <button className="bulk-btn" onClick={() => downloadData("student")}>
            Download All Students (.xlsx)
          </button>
        </div>
        <form className="bulk-form" onSubmit={handleStudentUpload}>
          <input
            type="file"
            accept=".xlsx, .xls"
            ref={studentInputRef}
            onChange={handleStudentFileChange}
            className="bulk-input"
          />
          <button
            className="bulk-btn"
            type="submit"
            disabled={studentUploading}
            style={{ background: "#3478f6" }}
          >
            {studentUploading ? "Uploading..." : "Upload Student Excel"}
          </button>
        </form>
        {studentMessage && <div className="bulk-message">{studentMessage}</div>}
        {uploadDetails.length > 0 && (
          <div style={{ color: "red", margin: "8px 0", fontSize: 13 }}>
            <div>Details:</div>
            <ul>
              {uploadDetails.slice(0, 10).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {uploadDetails.length > 10 && (
                <li>...and {uploadDetails.length - 10} more.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="bulk-section">
        <h3>2. Fee Bulk Upload/Export</h3>
        <div className="bulk-actions-row">
          <button className="bulk-btn" onClick={() => downloadTemplate("fee")}>
            Download Fee Excel Template
          </button>
          <button className="bulk-btn" onClick={() => downloadData("fee")}>
            Download All Fees (.xlsx)
          </button>
        </div>
        <form className="bulk-form" onSubmit={handleFeeUpload}>
          <input
            type="file"
            accept=".xlsx, .xls"
            ref={feeInputRef}
            onChange={handleFeeFileChange}
            className="bulk-input"
          />
          <button
            className="bulk-btn"
            type="submit"
            disabled={feeUploading}
            style={{ background: "#3478f6" }}
          >
            {feeUploading ? "Uploading..." : "Upload Fee Excel"}
          </button>
        </form>
        {feeMessage && <div className="bulk-message">{feeMessage}</div>}
      </div>
      <div style={{ marginTop: 28, fontSize: 13, color: "#888" }}>
        <b>Instructions:</b>
        <ul style={{ marginTop: 5 }}>
          <li>Download the template and fill in your data as per columns.</li>
          <li>Do not change column headers in the template.</li>
          <li>Upload only .xlsx or .xls files.</li>
          <li>For large data, upload in batches (e.g., 500–1000 rows at once).</li>
          <li>If you face errors, check all required columns and batch/student IDs.</li>
        </ul>
      </div>
    </div>
  );
}